import React, { useMemo, useState } from 'react';
import { H, X, Z } from './quantum/gates';
import { zeroState, basisState, applyGate, applyControlledGate, tensorProduct, measureQubit, blochVectorForQubit } from './quantum/QuantumState';
import BlochSphereCanvas from './BlochSphereCanvas.jsx';

const MESSAGE_STATES = {
  '0': { label: '|0⟩', state: zeroState(1) },
  '1': { label: '|1⟩', state: basisState(1, 1) },
  '+': { label: '|+⟩', state: applyGate(zeroState(1), H, 0, 1) },
  '-': { label: '|−⟩', state: applyGate(basisState(1, 1), H, 0, 1) },
};

const STEP_INFO = [
  {
    title: '1. Prepare the unknown state',
    desc: "Alice has qubit q0 in some state |ψ⟩ she wants to send to Bob. Nothing about the protocol requires her (or anyone) to know what α and β actually are.",
  },
  {
    title: '2. Create entanglement',
    desc: 'Alice and Bob share an entangled pair beforehand: q1 (Alice) and q2 (Bob) are prepared as (|00⟩+|11⟩)/√2 via H then CNOT.',
  },
  {
    title: '3. Bell-basis rotation',
    desc: "Alice entangles her unknown qubit q0 with her half of the pair, q1: a CNOT(q0→q1) followed by H on q0.",
  },
  {
    title: '4. Measure & send two classical bits',
    desc: 'Alice measures q0 and q1, getting two classical bits, and sends them to Bob over an ordinary classical channel (phone, radio, internet — nothing faster than light).',
  },
  {
    title: '5. Bob applies a correction',
    desc: "Depending on Alice's two bits, Bob applies X to q2 if the second bit is 1, and Z to q2 if the first bit is 1.",
  },
  {
    title: "6. Bob's qubit now matches Alice's original",
    desc: "q2's Bloch vector now matches q0's original Bloch vector exactly. Alice's original qubit was destroyed by her measurement (no-cloning is never violated) — only classical bits travelled, so nothing outran light.",
  },
];

export default function Teleportation() {
  const [msgId, setMsgId] = useState('+');
  const [step, setStep] = useState(0);
  const [measurement, setMeasurement] = useState(null); // { m0, m1 } once rolled

  const originalVector = useMemo(() => blochVectorForQubit(MESSAGE_STATES[msgId].state, 1, 0), [msgId]);

  // Deterministic states for steps 0-2 (no randomness yet).
  const preMeasureStates = useMemo(() => {
    const msg = MESSAGE_STATES[msgId].state;
    const s0 = tensorProduct(tensorProduct(msg, zeroState(1)), zeroState(1)); // q0=msg, q1=0, q2=0
    let s1 = applyGate(s0, H, 1, 3);
    s1 = applyControlledGate(s1, X, 1, 2, 3); // Bell pair on q1,q2
    let s2 = applyControlledGate(s1, X, 0, 1, 3);
    s2 = applyGate(s2, H, 0, 3); // Alice's Bell-basis rotation
    return [s0, s1, s2];
  }, [msgId]);

  const doMeasure = () => {
    const { result: m0, state: afterM0 } = measureQubit(preMeasureStates[2], 3, 0);
    const { result: m1, state: afterM1 } = measureQubit(afterM0, 3, 1);
    setMeasurement({ m0, m1, collapsed: afterM1 });
    setStep(3);
  };

  const correctedState = useMemo(() => {
    if (!measurement) return null;
    let s = measurement.collapsed;
    if (measurement.m1 === 1) s = applyGate(s, X, 2, 3);
    if (measurement.m0 === 1) s = applyGate(s, Z, 2, 3);
    return s;
  }, [measurement]);

  // Which state is "current" for display, depending on step.
  const currentState = step <= 2 ? preMeasureStates[step] : (step === 3 ? measurement.collapsed : correctedState);
  const bobVector = currentState ? blochVectorForQubit(currentState, 3, 2) : { x: 0, y: 0, z: 0 };

  const canGoNext = step < 5;
  const goNext = () => {
    if (step === 2 && !measurement) { doMeasure(); return; }
    setStep((s) => Math.min(5, s + 1));
  };
  const goPrev = () => setStep((s) => Math.max(0, s - 1));
  const reset = () => { setStep(0); setMeasurement(null); };
  const changeMessage = (id) => { setMsgId(id); reset(); };

  const bobMatches = step === 5 &&
    Math.abs(bobVector.x - originalVector.x) < 1e-6 &&
    Math.abs(bobVector.y - originalVector.y) < 1e-6 &&
    Math.abs(bobVector.z - originalVector.z) < 1e-6;

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-4xl font-bold text-gray-900 mb-1">Quantum Teleportation</h1>
          <p className="text-gray-600">
            Step through the real protocol. No matter travels, no information moves faster than light — Bob needs Alice's classical bits.
          </p>
        </div>

        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm mb-4">
          <h2 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Choose the unknown state to teleport</h2>
          <div className="flex flex-wrap gap-2">
            {Object.entries(MESSAGE_STATES).map(([id, { label }]) => (
              <button
                key={id}
                onClick={() => changeMessage(id)}
                className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${msgId === id ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-50 text-gray-700 border border-gray-200 hover:border-blue-300'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm mb-4">
          <div className="flex items-center gap-2 mb-2">
            {STEP_INFO.map((_, i) => (
              <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-blue-600' : 'bg-gray-200'}`} />
            ))}
          </div>
          <h2 className="text-lg font-bold text-gray-900 mb-1">{STEP_INFO[step].title}</h2>
          <p className="text-sm text-gray-600">{STEP_INFO[step].desc}</p>

          {step === 3 && measurement && (
            <div className="mt-3 flex justify-center gap-6 font-mono text-sm bg-gray-50 rounded-lg p-3 border border-gray-200">
              <span>Alice measured q0 = <strong className="text-blue-700">{measurement.m0}</strong></span>
              <span>Alice measured q1 = <strong className="text-blue-700">{measurement.m1}</strong></span>
            </div>
          )}
          {step === 4 && measurement && (
            <div className="mt-3 flex justify-center gap-6 font-mono text-sm bg-gray-50 rounded-lg p-3 border border-gray-200">
              <span>{measurement.m1 === 1 ? 'Apply X to q2 ✓' : 'No X needed (bit was 0)'}</span>
              <span>{measurement.m0 === 1 ? 'Apply Z to q2 ✓' : 'No Z needed (bit was 0)'}</span>
            </div>
          )}

          <div className="flex justify-center gap-2 mt-4">
            <button onClick={goPrev} disabled={step === 0} className="px-4 py-1.5 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 font-semibold disabled:opacity-40">
              ← Previous
            </button>
            <button onClick={goNext} disabled={!canGoNext} className="px-4 py-1.5 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:opacity-40">
              {step === 2 && !measurement ? 'Measure →' : 'Next →'}
            </button>
            <button onClick={reset} className="px-4 py-1.5 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 font-semibold">
              ↻ Reset
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm text-center">
            <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Alice's original |ψ⟩ (q0)</h3>
            <div className="flex justify-center">
              <BlochSphereCanvas vector={originalVector} size={180} showMixed={false} />
            </div>
            <p className="text-xs text-gray-500 mt-2">Shown for reference — this is what we're trying to reproduce at Bob's end.</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm text-center">
            <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Bob's qubit (q2)</h3>
            <div className="flex justify-center">
              <BlochSphereCanvas vector={bobVector} size={180} />
            </div>
            {bobMatches && (
              <p className="text-sm text-green-700 font-semibold mt-2">✓ Matches Alice's original state exactly</p>
            )}
            {!bobMatches && step < 5 && (
              <p className="text-xs text-gray-500 mt-2">Not yet teleported.</p>
            )}
          </div>
        </div>

        <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-100 text-sm text-blue-800">
          <strong>Not faster-than-light, not cloning:</strong> Bob's qubit only becomes usable once he receives Alice's two classical bits (step 4→5) and applies the matching correction — that
          communication is limited by the speed of light like any other. And Alice's own copy of |ψ⟩ was destroyed the moment she measured q0 and q1, so no state was ever duplicated.
        </div>
      </div>
    </div>
  );
}
